import { Payment } from "@crypto-payment-gateway/models"
import {
	CancelationReason,
	ConfirmationReason,
	OrderActionType,
} from "../../../../../hooks/mutateOrderAction"
import { Store } from "../../../../../hooks/useFetchStores"
import {
	getPaymentCommission,
	getPaymentCommissionArs,
	getPaymentCreditedAmount,
	getPaymentCreditedAmountArs,
	getPaymentCurrency,
	getPaymentExpectedAmount,
	getPaymentNetwork,
	getPaymentReceivedAmount,
	getPaymentReceivedAmountArs,
	getPaymentReceivedAmountInRequestedCurrency,
} from "../../../../../lib/utils"
import {
	PaymentHashTable,
	ShopifyInfo,
	StoreInfo,
	TableOrder,
	TableOrderArs,
	TableOrderSubTransactionArs,
	TiendanubeInfo,
	WoocommerceInfo,
	ProcessingResult,
	TaxDescription,
	TableOrderSubTransactionCrypto,
	CustomStore,
} from "../types"
import {
	convertToTime,
	createErrorTime,
	extractPartyInfoArs,
	extractTransactionPartiesArs,
	extractTransactionPartiesCrypto,
	extractTransactionTaxesFromOrder,
	isTransferPayment,
} from "../utils"

export interface ProcessedPayments {
	order: TableOrder[]
	hashtable: PaymentHashTable
}

const WORKER_COUNT = 4 // Adjust based on available cores
const CHUNK_SIZE = 100

export function mapCombinedPaymentsToTableOrder({
	payments,
	transactions,
	stores,
	onProcessed,
}: {
	payments: (Payment & { id: string })[] | undefined
	transactions: any
	stores: Store[]
	onProcessed?: (result: ProcessingResult) => void
}): Promise<{ order: TableOrder[]; hashtable: PaymentHashTable }> {
	if (!transactions || !payments) {
		return Promise.resolve({ order: [], hashtable: {} })
	}

	return new Promise((resolve) => {
		const finalResult = {
			order: [] as TableOrder[],
			hashtable: {} as PaymentHashTable,
		}
		let completedPayments = 0
		let activeWorkers = 0
		let currentChunkIndex = 0
		let processedChunks = 0

		const chunks: Array<(Payment & { id: string })[]> = []
		for (let i = 0; i < payments.length; i += CHUNK_SIZE) {
			chunks.push(payments.slice(i, i + CHUNK_SIZE))
		}

		const checkCompletion = () => {
			if (completedPayments === payments.length && activeWorkers === 0) {
				finalResult.order.sort(
					(a, b) => b.time.time.getTime() - a.time.time.getTime()
				)
				resolve(finalResult)
			}
		}

		const startNextWorker = () => {
			if (currentChunkIndex >= chunks.length || activeWorkers >= WORKER_COUNT) {
				checkCompletion()
				return
			}

			const chunk = chunks[currentChunkIndex++]
			const worker = new Worker(
				new URL("../paymentProcessor.worker.ts", import.meta.url)
			)
			activeWorkers++

			let chunkCompletedPayments = 0

			worker.onmessage = (e: MessageEvent<ProcessingResult>) => {
				const result = e.data
				completedPayments++
				chunkCompletedPayments++

				onProcessed?.(result)

				finalResult.order.push(result.order)
				finalResult.hashtable[result.hashtableEntry[0]] =
					result.hashtableEntry[1]

				if (chunkCompletedPayments === chunk.length) {
					processedChunks++
					worker.terminate()
					activeWorkers--
					startNextWorker()
				}
			}

			worker.onerror = (error) => {
				worker.terminate()
				activeWorkers--
				currentChunkIndex--
				startNextWorker()
			}

			worker.postMessage({ payments: chunk, stores })
		}

		for (let i = 0; i < Math.min(WORKER_COUNT, chunks.length); i++) {
			startNextWorker()
		}
	})
}

export function mapToTableOrderSubTransaction(
	transaction: any
): TableOrderSubTransactionCrypto {
	const percent = transaction.commission / 1.21
	const commision_amount =
		transaction.commission_amount ??
		parseFloat(transaction.amountReadable) * (percent / 100)
	const credited_amount = transaction.amountReadable - commision_amount
	return {
		network: transaction.network,
		currency: transaction.currency,
		amount: transaction.amount,
		receivedInRequestedCurrency: transaction.amountReadable,
		creditedInRequestedCurrency: {
			amount: credited_amount,
			currency: transaction.currency,
		},
		received: {
			amount: transaction.amountReadable,
			currency: transaction.currency,
		},
		credited: {
			amount: credited_amount,
			currency: transaction.currency,
		},
		commission: {
			commission: {
				amount: commision_amount,
				currency: transaction.currency,
			},
			percent: percent,
		},
		transactionHash: transaction.transaction_hash,
		transactionId: transaction.transaction_id,
		sender: {
			address: transaction.sender_address,
			type: "crypto",
		},
		time: convertToTime(transaction.creation_timestamp) ?? createErrorTime(),
	}
}

export function mapToTableOrderSubTransactionArs(
	transaction: any
): TableOrderSubTransactionArs {
	const sender = extractPartyInfoArs(transaction, "sender")
	return {
		network: transaction.network,
		currency: transaction.currency,
		amount: transaction.amount,
		received: {
			amount: transaction.amount,
			currency: transaction.currency,
		},
		credited: {
			amount: transaction.credited_amount,
			currency: transaction.currency,
		},
		commission: {
			commission: {
				amount: transaction.commission_amount,
				currency: transaction.currency,
			},
			commissionInRequestedCurrency: {
				amount: transaction.commission_amount,
				currency: transaction.currency,
			},
			percent: transaction.commission,
			commissionTaxes: transaction.commission_taxes
				? transaction.commission_taxes.map((tax: TaxDescription) => ({
						tax_amount: tax.tax_amount,
						tax_type: tax.tax_type,
						tax_rate: tax.tax_rate,
				  }))
				: undefined,
			commission_tax_total_amount: transaction.commission_tax_amount,
		},
		transactionId: transaction.transaction_id,
		sender,
		time: convertToTime(transaction.creation_timestamp) ?? createErrorTime(),
	}
}

export function mapToTableOrderArs(
	payment: Payment,
	stores: Store[]
): TableOrderArs {
	const store = getStoreObject(payment)
	const { sender, recipient } = extractTransactionPartiesArs(payment)
	const taxes = extractTransactionTaxesFromOrder(payment)

	return {
		id: payment.id ?? "-",
		time: convertToTime(payment.creation_timestamp) ?? createErrorTime(),
		lastModifiedDateTime: convertToTime(payment.last_modified_timestamp),
		creationDateTime: convertToTime(payment.creation_timestamp),
		display_id:
			store?.store_data.display_id ??
			payment.external_id ??
			payment.customer_email,
		currency: getPaymentCurrency(payment) ?? "-",
		currencyType: "fiat",
		expected: payment.price,
		received: getPaymentReceivedAmountArs(payment),
		credited: getPaymentCreditedAmountArs(payment),
		amountDisplayInTable: payment.price.amount.toString(),
		status: payment.payment_status ?? "-",
		commission: getPaymentCommissionArs(payment),
		network: getPaymentNetwork(payment),
		sender,
		recipient,
		transactions: payment.transactions
			? payment.transactions.map(mapToTableOrderSubTransactionArs)
			: [],
		store: store?.store_data,
		payment_options: payment.payment_options as [],
		paymentUrl: payment.payment_url,
		expiration_timestamp: convertToTime(payment.expiration_timestamp),
		taxes: taxes,
		tableView: "orders",
		...getAccountStoreDetailsFromId(payment, stores),
	}
}

export function mapToTableOrder(payment: any, stores: Store[]): TableOrder {
	return isTransferPayment(payment)
		? mapToTableOrderArs(payment, stores)
		: mapToTableTransactionCrypto(payment, stores)
}

export function mapToTableTransactionCrypto(
	payment: any,
	stores: Store[]
): TableOrder {
	const quote = payment.quotes!.find(
		(q: any) =>
			q.currency === payment.transaction_fields?.currency &&
			q.network === payment.transaction_fields?.network
	)
	const received = getPaymentReceivedAmount(payment)
	const receivedInRequestedCurrency = quote
		? getPaymentReceivedAmountInRequestedCurrency(payment, quote)
		: undefined
	const commission = quote ? getPaymentCommission(payment, quote) : undefined
	const credited = getPaymentCreditedAmount(received, commission?.commission)
	const creditedInRequestedCurrency = getPaymentCreditedAmount(
		receivedInRequestedCurrency,
		commission?.commissionInRequestedCurrency
	)
	const { sender, recipient } = extractTransactionPartiesCrypto(payment)
	const store = getStoreObject(payment)
	return {
		id: payment.id,
		lastModifiedDateTime: convertToTime(payment.last_modified_timestamp),
		creationDateTime: convertToTime(payment.creation_timestamp),
		time: convertToTime(payment.creation_timestamp) ?? createErrorTime(),
		currencyType: "crypto",
		currency: getPaymentCurrency(payment) ?? "ARS",
		expected: getPaymentExpectedAmount(payment, quote),
		received: received,
		receivedInRequestedCurrency: receivedInRequestedCurrency,
		credited: credited,
		creditedInRequestedCurrency: creditedInRequestedCurrency,
		amountDisplayInTable: payment.price.amount.toString(),
		status: payment.payment_status ?? "-",
		commission: commission,
		network: getPaymentNetwork(payment),
		transactions: payment.transactions?.map(mapToTableOrderSubTransaction),
		transactionHash: payment.transaction_fields?.transaction_hash,
		store: store?.store_data,
		display_id:
			store?.store_data.display_id ??
			payment.external_id ??
			payment.customer_email,
		paymentUrl: payment.payment_url,
		payment_options: payment.payment_options ?? undefined,
		sender,
		recipient,
		tableView: "orders",
		...getAccountStoreDetailsFromId(payment, stores),
	}
}

export function getAccountStoreDetailsFromId(
	payment: any,
	stores: Store[]
): { store_id: number | undefined; store_name: string | undefined } {
	try {
		const store_id = getStoreId(payment)
		const store = stores.find(
			(store) => String(store.store_id) === String(store_id)
		)
		return {
			store_id,
			store_name: store ? store.store_name : undefined,
		}
	} catch (error) {
		console.error("Error getting store details:", error)
		return {
			store_id: undefined,
			store_name: undefined,
		}
	}
}

export function storeContainsAction(data: any) {
	if (data.confirm_payload) {
		return {
			type: "confirm" as OrderActionType,
			reason: data.confirm_payload.reason as ConfirmationReason,
		}
	}

	if (data.cancel_payload) {
		return {
			type: "cancel" as OrderActionType,
			reason: data.cancel_payload.reason as CancelationReason,
		}
	}

	return undefined
}

export function getOrderStatus(
	payment: TableOrder | undefined
):
	| { type: OrderActionType; reason: ConfirmationReason | CancelationReason }
	| undefined {
	if (!payment) {
		return undefined
	}
	var store = getStoreObject(payment)

	if (store?.store_data) {
		var action = storeContainsAction(store.store_data)
		if (action) {
			return action
		}
	}
	return undefined
}

function getStoreId(payment: any): number | undefined {
	switch (true) {
		case !!payment.tiendanube:
			return payment.tiendanube.store_id
		case !!payment.woocommerce:
			return payment.woocommerce.store_id
		case !!payment.shopify:
			return payment.shopify.store_id
		default:
			// Search all nested objects for store_id
			for (const key in payment) {
				if (
					payment[key] &&
					typeof payment[key] === "object" &&
					payment[key].store_id
				) {
					return payment[key].store_id
				}
			}
			return undefined
	}
}

export function getStoreObject(payment: Payment | TableOrder):
	| {
			store_id?: string | number
			store_data: StoreInfo
	  }
	| undefined {
	if ("store" in payment && payment.store) {
		return {
			store_id: getStoreId(payment),
			store_data: payment.store,
		}
	}

	if ("tiendanube" in payment && payment.tiendanube) {
		const storeData: TiendanubeInfo & { store_type: "tiendanube" } = {
			...payment.tiendanube,
			display_id: payment.tiendanube.order_number,
			order_number: payment.tiendanube.order_number,
			order_id: payment.tiendanube.order_id,
			store_type: "tiendanube",
		}
		return {
			store_id: payment.tiendanube.store_id,
			store_data: storeData,
		}
	}

	if ("woocommerce" in payment && payment.woocommerce) {
		const storeData: WoocommerceInfo & { store_type: "woocommerce" } = {
			...payment.woocommerce,
			display_id: payment.woocommerce.order_id,
			store_type: "woocommerce",
		}
		return {
			store_id: payment.woocommerce.store_id,
			store_data: storeData,
		}
	}

	if ("shopify" in payment && payment.shopify) {
		const storeData: ShopifyInfo & { store_type: "shopify" } = {
			...payment.shopify,
			display_id: payment.shopify.order_name,
			order_name: payment.shopify.order_name,
			order_id: payment.shopify.order_id,
			store_type: "shopify",
		}
		return {
			store_id: payment.shopify.store_id,
			store_data: storeData,
		}
	}

	if ("external_id" in payment && payment.external_id) {
		const storeData: CustomStore = {
			store_id: undefined,
			display_id: payment.external_id,
			store_type: "custom",
		}
		return {
			store_data: storeData,
			store_id: undefined,
		}
	}

	return undefined
}

export function setNewOrderStatus(
	payment: TableOrder,
	status: {
		type: OrderActionType
		reason: ConfirmationReason | CancelationReason
	}
): TableOrder {
	const store = { ...payment.store }
	const payload =
		status.type === "confirm"
			? { confirm_payload: { reason: status.reason } }
			: { cancel_payload: { reason: status.reason } }

	// Update the store data with the new payload
	payment.store = {
		...store,
		...payload,
	} as StoreInfo

	return payment
}
